import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import './Auth.css';

function Register() {
    const [email, setEmail] = useState('');
    const [orgName, setOrgName] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [privacyConsent, setPrivacyConsent] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError('Email address is required');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please provide a valid email address');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (!privacyConsent) {
            setError('You must agree to the privacy policy to create an account');
            return;
        }

        setLoading(true);

        try {
            await register(email, username, password, orgName);
            navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-header">
                    <div className="auth-logo">
                        <div className="logo-icon">
                            <svg width="32" height="32" viewBox="0 0 100 100" fill="none">
                                <defs>
                                    {/* Background gradient */}
                                    <linearGradient id="registerBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#22d3ee" />
                                        <stop offset="50%" stopColor="#3b82f6" />
                                        <stop offset="100%" stopColor="#8b5cf6" />
                                    </linearGradient>
                                    {/* Checkmark gradient */}
                                    <linearGradient id="registerCheckGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#06b6d4" />
                                        <stop offset="50%" stopColor="#3b82f6" />
                                        <stop offset="100%" stopColor="#a855f7" />
                                    </linearGradient>
                                </defs>

                                {/* Main container with rounded corners */}
                                <rect x="10" y="15" width="80" height="72" rx="12" ry="12" fill="url(#registerBgGradient)" />

                                {/* Grid cells - top row (filled) */}
                                <rect x="15" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                                <rect x="40" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
                                <rect x="65" y="20" width="20" height="10" rx="2" fill="rgba(255,255,255,0.3)" />

                                {/* Grid cells - middle rows (white) */}
                                <rect x="15" y="34" width="20" height="10" rx="2" fill="white" />
                                <rect x="40" y="34" width="20" height="10" rx="2" fill="white" />
                                <rect x="65" y="34" width="20" height="10" rx="2" fill="white" />

                                <rect x="15" y="48" width="20" height="10" rx="2" fill="white" />
                                <rect x="40" y="48" width="20" height="10" rx="2" fill="white" />
                                <rect x="65" y="48" width="20" height="10" rx="2" fill="white" />

                                {/* Grid cells - bottom row (white) */}
                                <rect x="15" y="62" width="20" height="10" rx="2" fill="white" />
                                <rect x="40" y="62" width="20" height="10" rx="2" fill="white" />
                                <rect x="65" y="62" width="20" height="10" rx="2" fill="white" />

                                {/* Large checkmark */}
                                <path d="M50 53 L65 68 L95 30"
                                    stroke="url(#registerCheckGradient)"
                                    strokeWidth="8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none" />
                            </svg>
                        </div>
                        <span className="logo-text">Spreadsheetr</span>
                    </div>
                    <h1>Create Your Spreadsheetr Business Account</h1>
                    <p>Establish ownership and secure your organization's logic.</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {error && (
                        <div className="alert alert-error">
                            {error}
                        </div>
                    )}

                    <div className="alert alert-info" style={{ fontSize: '0.85rem', marginBottom: 'var(--spacing-4)' }}>
                        <strong>Admin Setup:</strong> This creates the Organization Owner. Operators are invited later.
                    </div>

                    <div className="form-group">
                        <label className="form-label">Email Address</label>
                        <input
                            type="email"
                            className="form-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@yourcompany.com"
                            required
                        />
                        <small className="form-help">This will be your login email and used for account recovery</small>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Organization Name</label>
                        <input
                            type="text"
                            className="form-input"
                            value={orgName}
                            onChange={(e) => setOrgName(e.target.value)}
                            placeholder="e.g. Acme Finance"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Username (Admin)</label>
                        <input
                            type="text"
                            className="form-input"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="e.g. admin_user"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Password</label>
                        <input
                            type="password"
                            className="form-input"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Confirm Password</label>
                        <input
                            type="password"
                            className="form-input"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm your password"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={privacyConsent}
                                onChange={(e) => setPrivacyConsent(e.target.checked)}
                                required
                            />
                            <span className="checkbox-text">
                                I agree to the <Link to="/privacy" target="_blank" className="privacy-link">Privacy Policy</Link> and
                                understand that my email, username and organization name will be stored securely.
                            </span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        style={{ width: '100%' }}
                        disabled={loading}
                    >
                        {loading ? <span className="spinner"></span> : 'Create Organization'}
                    </button>
                </form>

                <div className="auth-footer">
                    <p>
                        Already have an account?{' '}
                        <Link to="/login">Sign in</Link>
                    </p>
                </div>
            </div>

            <div className="auth-background">
                <div className="auth-bg-content">
                    <h2>Setup Organization</h2>
                    <ul>
                        <li>Establish Namespace</li>
                        <li>Create Empty Template Registry</li>
                        <li>Initialize Immutable Audit Log</li>
                        <li>Audit Structural Integrity</li>
                        <li>Custom Output Formatting</li>
                        <li>No Demo Data. No Fluff.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default Register;
